import type { RecentSwap, RecentSwapStepView } from './contracts'
import { ChainId } from '../domain/flow'
import {
  TON_TRANSACTION_EXPLORER_URL,
  TRANSACTION_EXPLORER_URL_BY_CHAIN_ID,
} from '../shared/transaction-explorers'
export { presentSwapSteps } from './step-presenter'
export type { RecentSwap, RecentSwapStepView } from './contracts'

export const isResumable = (swap: RecentSwap): boolean => {
  const firstIncomplete = swap.progress.steps.find(
    (step) => step.status !== 'completed' && step.status !== 'cancelled'
  )
  return (
    !!firstIncomplete &&
    ['usdt_to_ton', 'ton_to_spotty'].includes(firstIncomplete.type) &&
    firstIncomplete.status !== 'submitted' &&
    firstIncomplete.status !== 'reconciling' &&
    !(firstIncomplete.status === 'failed' && firstIncomplete.failure_kind === 'terminal')
  )
}

const usdtToTonDone = (swap: RecentSwap): boolean =>
  swap.progress.steps.some((step) => step.type === 'usdt_to_ton' && step.status === 'completed')

export const atFinalSwapStep = (swap: RecentSwap): boolean =>
  swap.bridge_output_token === 'TON' || usdtToTonDone(swap)

export const continuationStepLabel = (swap: RecentSwap): string => {
  const firstIncomplete = swap.progress.steps.find(
    (step) => step.status !== 'completed' && step.status !== 'cancelled'
  )
  return firstIncomplete && ['usdt_to_ton', 'ton_to_spotty'].includes(firstIncomplete.type)
    ? 'Continue on TON'
    : 'Complete next step'
}

const EVM_CHAIN_NAMES: Record<number, string> = {
  1: 'Ethereum',
  42161: 'Arbitrum',
  8453: 'Base',
  10: 'Optimism',
}

export const statusBadge = (status: string) => {
  switch (status) {
    case 'completed':
      return 'bg-[#16a34a]/20 text-[#16a34a]'
    case 'failed':
      return 'bg-[#b42318]/20 text-[#b42318]'
    default:
      return 'bg-[#1980e6]/20 text-[#1980e6]'
  }
}

export const statusLabel = (swap: RecentSwap) => {
  const firstIncomplete = swap.progress.steps.find(
    (step) => step.status !== 'completed' && step.status !== 'cancelled'
  )
  if (firstIncomplete?.type === 'bridge' && firstIncomplete.status === 'reconciling') {
    return 'Checking transaction...'
  }
  if (
    firstIncomplete?.type === 'bridge' &&
    firstIncomplete.status === 'failed' &&
    firstIncomplete.failure_code === 'evm_bridge_not_broadcast'
  ) {
    return 'Retry bridge available'
  }
  if (firstIncomplete && ['usdt_to_ton', 'ton_to_spotty'].includes(firstIncomplete.type)) {
    if (['submitted', 'reconciling'].includes(firstIncomplete.status)) {
      return 'Checking TON transaction...'
    }
    if (swap.status === 'swapping' || swap.status === 'needs_attention') return 'Continue on TON'
  }
  if (swap.status === 'needs_attention') {
    return 'Action needed'
  }
  switch (swap.status) {
    case 'pending':
      return 'Pending'
    case 'bridging':
      return 'Bridging...'
    case 'swapping':
      return 'Swapping...'
    case 'completed':
      return 'Completed'
    case 'failed':
      return 'Failed'
    case 'cancelled':
      return 'Cancelled'
    default:
      return swap.status
  }
}

const txTypeLabel = (type: string) => {
  switch (type) {
    case 'bridge':
      return 'Bridge'
    case 'swap_usdt_ton':
      return 'USDT → GRAM'
    case 'swap_ton_spotty':
      return 'GRAM → SPOTTY'
    default:
      return type
  }
}

export const txChainLabel = (tx: RecentSwapStepView) => {
  if (tx.chain_type === 'ton') return 'TON'
  if (tx.chain_type === 'solana') return 'Solana'
  if (tx.chain_type === 'evm' && tx.chain_id) {
    return EVM_CHAIN_NAMES[tx.chain_id] ?? `Chain ${String(tx.chain_id)}`
  }
  return tx.chain_type
}

export const txExplorerUrl = (tx: RecentSwapStepView) => {
  if (tx.chain_type === 'ton') {
    return `${TON_TRANSACTION_EXPLORER_URL}${tx.tx_hash}`
  }
  if (tx.chain_type === 'solana') {
    const base = TRANSACTION_EXPLORER_URL_BY_CHAIN_ID[ChainId.SOLANA]
    return base ? `${base}${tx.tx_hash}` : '#'
  }
  if (tx.chain_type === 'evm' && tx.chain_id) {
    const base = TRANSACTION_EXPLORER_URL_BY_CHAIN_ID[tx.chain_id]
    if (base) return `${base}${tx.tx_hash}`
  }
  return '#'
}

export const formatDate = (iso: string) => {
  const d = new Date(iso)
  return (
    d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  )
}

/** Step icon kind decided from a backend transaction row's status. */
export type StepIconKind = 'completed' | 'failed' | 'active' | 'pending'

export const stepIconKind = (tx: RecentSwapStepView, parentSwapStatus: string): StepIconKind => {
  if (tx.status === 'completed') return 'completed'
  if (tx.status === 'failed') return 'failed'
  // The aggregate wins over stale in-flight step snapshots.
  if (parentSwapStatus === 'completed') return 'completed'
  if (parentSwapStatus === 'failed') return 'pending'
  if (tx.status === 'active' || tx.status === 'pending') {
    return tx.tx_hash ? 'active' : 'pending'
  }
  return 'pending'
}

const formatDurationCompact = (ms: number): string => {
  const totalSec = Math.round(ms / 1000)
  if (totalSec < 60) return `${String(totalSec)}s`
  const min = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  return sec === 0 ? `${String(min)}m` : `${String(min)}m ${String(sec)}s`
}

export const stepDurationLabel = (tx: RecentSwapStepView): string => {
  if (!tx.started_at) return ''
  if (!tx.completed_at) return 'in progress'
  const ms = new Date(tx.completed_at).getTime() - new Date(tx.started_at).getTime()
  if (ms <= 0) return ''
  return formatDurationCompact(ms)
}

export const stepLabel = (tx: RecentSwapStepView, swap: RecentSwap): string => {
  const providerSource = tx.provider ? tx.provider : swap.bridge_provider
  const provider = providerSource.trim()
  const providerTitled = provider
    ? provider.charAt(0).toUpperCase() + provider.slice(1)
    : 'provider'
  switch (tx.tx_type) {
    case 'bridge':
      return `Bridge via ${providerTitled}`
    case 'swap_usdt_ton':
      return 'Swap USDT → GRAM via STON.fi'
    case 'swap_ton_spotty':
      return 'Swap GRAM → SPOTTY via STON.fi'
    case 'jupiter_swap':
      return `Swap ${swap.source_token_symbol} → USDT via Jupiter`
    default:
      return txTypeLabel(tx.tx_type)
  }
}

export const formatExpectedVsActual = (tx: RecentSwapStepView): string => {
  const actualIn = tx.amount_in || tx.amount_in_expected
  const actualOut = tx.amount_out
  const expectedOut = tx.amount_out_expected
  if (!actualIn || (!actualOut && !expectedOut)) return 'Amount data unavailable'
  const outputLabel = actualOut || `~${expectedOut}`
  const main = `${actualIn} → ${outputLabel}`
  if (expectedOut && expectedOut !== actualOut) {
    return actualOut ? `${main} (expected ~${expectedOut})` : main
  }
  return main
}

/** "0x4a3f...c9b1" / "UQCksv...Fyeo9" — middle ellipsis. */
export const truncateAddress = (addr: string, head = 6, tail = 4): string => {
  if (!addr) return ''
  if (addr.length <= head + tail + 2) return addr
  return `${addr.slice(0, head)}...${addr.slice(-tail)}`
}

/** "0x4a3f...c9b1 (Arbitrum)" — truncated address + chain hint suffix. */
export const formatAddressWithChain = (
  addr: string,
  chainType: string,
  chainId: number | null | undefined
): string => {
  if (!addr) return ''
  const tr = truncateAddress(addr)
  if (chainType === 'ton') return `${tr} (TON)`
  if (chainType === 'solana') return `${tr} (Solana)`
  if (chainType === 'evm' && chainId) {
    const name = EVM_CHAIN_NAMES[chainId] ?? `Chain ${String(chainId)}`
    return `${tr} (${name})`
  }
  return tr
}
